import React, { useContext } from 'react';
import { CardContent, Container } from '@mui/material';
import { AutoAwesome } from '@mui/icons-material';

import { ReactComponent as ScenarioManagerIcon } from '../../assets/images/scenario.svg';
import { ReactComponent as ExtractIcon } from '../../assets/images/extract.svg';
import { ReactComponent as ReportsIcon } from '../../assets/images/reports.svg';
import { ReactComponent as CustomChartIcon } from '../../assets/images/custom_chart.svg';
import { ReactComponent as AdminIcon } from '../../assets/images/admin.svg';
import { isTrinityUser } from '../../utils/user';
import { filterSearch } from '../../utils/misc';

import ScenarioManager from '../ScenarioManager';
import Extracts from '../Extracts';
import AdminTools from '../Admin';
import AgenticV2 from '../AgenticV2';
import ExecutiveView from '../ExecutiveView';

import { Profile } from '../../types/models';

import CustomChart from '../CustomChart';

import Reports from '../Reports';

import ForecastEdgeAITab from './ForecastEdgeAITab';

import { ProfileContext } from './AuthController';

export interface IRouteParams {
  pathName: string;
  search: string;
  label: string;
  component: JSX.Element;
  protected: boolean;
  exact: boolean;
  id: string;
  icon: JSX.Element;
}

const RouteParams = (
  search?: string
): {
  routes: IRouteParams[];
  scenarioManagerRoute: IRouteParams;
  defaultRoute: IRouteParams;
} => {
  const profile = useContext(ProfileContext);
  if (profile === undefined) {
    throw Error('Profile is required to render NavigationBar');
  }

  const showForAdmin = isTrinityUser((profile as Profile).User);

  const scenarioManagerRoute: IRouteParams = {
    id: 'scenarioManager',
    pathName: '/scenario-manager',
    search: filterSearch(search, ['client', 'model', 'instance', 'scenario']),
    label: 'Scenario Manager',
    component: (
      <Container maxWidth={false} id="scenarioManager">
        <CardContent>
          <ScenarioManager />
        </CardContent>
      </Container>
    ),
    icon: <ScenarioManagerIcon />,
    protected: false,
    exact: true,
  };

  const reportsRoute: IRouteParams = {
    id: 'reports',
    pathName: '/reports',
    search: filterSearch(search, ['client', 'model', 'instance']),
    label: 'Reports',
    component: (
      <Container maxWidth={false} id="reports">
        <CardContent>
          <Reports />
        </CardContent>
      </Container>
    ),
    protected: (profile as Profile).User.Extras_Flag === 1,
    exact: true,
    icon: <ReportsIcon />,
  };

  // HasExecutiveAccess is a 0/1/2 enum: 1 = executive-ONLY experience,
  // 2 = executive + traditional. Level-1 users (true executives) see
  // exactly two tabs — Reports (normal experience) and the Executive
  // View under the ForecastEDGE AI label — and land on the Executive
  // View. This branch intentionally bypasses every other tab regardless
  // of what other flags the user carries. Level-2 users fall through to
  // the normal tab set; their executive surface hangs off the
  // ForecastEDGE AI tab below.
  if ((profile as Profile).User.HasExecutiveAccess === 1) {
    const executiveRoute: IRouteParams = {
      id: 'executiveAI',
      pathName: '/executive',
      search: filterSearch(search, ['client']),
      label: 'ForecastEDGE AI',
      component: <ExecutiveView />,
      protected: false,
      exact: true,
      icon: <AutoAwesome />,
    };
    return {
      routes: [reportsRoute, executiveRoute],
      scenarioManagerRoute,
      defaultRoute: executiveRoute,
    };
  }

  const routes: IRouteParams[] = [
    scenarioManagerRoute,
    {
      id: 'extract',
      pathName: '/extract',
      search: filterSearch(search, ['client', 'model', 'instance']),
      label: 'Extract',
      component: (
        <Container maxWidth={false} id="extract">
          <CardContent>
            <Extracts />
          </CardContent>
        </Container>
      ),
      protected: (profile as Profile).User.Extras_Flag === 1,
      exact: true,
      icon: <ExtractIcon />,
    },
    reportsRoute,
    {
      id: 'customChart',
      pathName: '/customChart',
      search: filterSearch(search, ['client', 'model', 'instance']),
      label: 'Custom Chart ',
      component: (
        <Container maxWidth={false} id="customChart">
          <CardContent>
            <CustomChart />
          </CardContent>
        </Container>
      ),
      protected: (profile as Profile).User.Extras_Flag === 1,
      exact: false,
      icon: <CustomChartIcon />,
    },
  ];

  if (showForAdmin) {
    routes.push({
      id: 'admin',
      pathName: '/admin',
      search: filterSearch(search, ['client', 'model']),
      label: 'Admin Tools',
      component: (
        <Container maxWidth={false} id="admin">
          <CardContent>
            <AdminTools />
          </CardContent>
        </Container>
      ),
      protected: false,
      exact: true,
      icon: <AdminIcon />,
    });
  }

  // ForecastEDGE AI tab. Which surface it hosts depends on the two
  // access flags (executive-only level-1 users never reach here — they
  // took the early-return branch above):
  //   agentic only          → analyst surface (AgenticV2)
  //   agentic + executive 2 → in-place toggle between both surfaces
  //   executive 2 only      → executive surface alone
  const hasAgentic = (profile as Profile).User.HasAgenticAccess === 1;
  const hasAdditiveExecutive =
    (profile as Profile).User.HasExecutiveAccess === 2;
  if (hasAgentic || hasAdditiveExecutive) {
    let aiComponent: React.ReactElement;
    if (hasAgentic && hasAdditiveExecutive) {
      aiComponent = <ForecastEdgeAITab />;
    } else if (hasAgentic) {
      aiComponent = <AgenticV2 />;
    } else {
      aiComponent = <ExecutiveView />;
    }
    routes.push({
      id: 'forecastEdgeAI',
      pathName: '/agentic-v2',
      search: filterSearch(search, ['client', 'model']),
      label: 'ForecastEDGE AI',
      component: aiComponent,
      protected: false,
      exact: true,
      icon: <AutoAwesome />,
    });
  }

  return { routes, scenarioManagerRoute, defaultRoute: scenarioManagerRoute };
};

export default RouteParams;
